import React from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Download, FileText, Table, Code, Globe, FileSpreadsheet, ChevronDown } from 'lucide-react';
import { ReportFormat } from '@/hooks/useMultiFormatReportGeneration';

interface ReportFormatSelectorProps {
  onGenerateReport: (format: ReportFormat) => void;
  isGenerating: boolean;
  disabled?: boolean;
}

const formatOptions = [
  {
    format: 'txt' as ReportFormat,
    label: 'Text Report',
    description: 'Plain text format',
    icon: FileText
  },
  {
    format: 'csv' as ReportFormat,
    label: 'CSV Export',
    description: 'Spreadsheet compatible',
    icon: Table
  },
  {
    format: 'json' as ReportFormat,
    label: 'JSON Export',
    description: 'Structured data format',
    icon: Code
  },
  {
    format: 'html' as ReportFormat,
    label: 'HTML Report',
    description: 'Web format with styling',
    icon: Globe
  },
  {
    format: 'xlsx' as ReportFormat,
    label: 'Excel Format',
    description: 'Excel/Google Sheets',
    icon: FileSpreadsheet
  }
];

export const ReportFormatSelector: React.FC<ReportFormatSelectorProps> = ({
  onGenerateReport,
  isGenerating,
  disabled = false
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          disabled={disabled || isGenerating}
          className="min-w-[160px]"
          variant="default"
        >
          <Download className="mr-2 h-4 w-4" />
          {isGenerating ? 'Generating...' : 'Download Report'}
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Choose Report Format</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {formatOptions.map(({ format, label, description, icon: Icon }) => (
          <DropdownMenuItem
            key={format}
            onClick={() => onGenerateReport(format)}
            disabled={isGenerating}
            className="flex items-start gap-3 p-3 cursor-pointer"
          >
            <Icon className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <div className="flex flex-col">
              <span className="font-medium">{label}</span>
              <span className="text-xs text-muted-foreground">{description}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};